import React, { useState } from 'react'

interface StarRatingProps {
  rating: number
  interactive?: boolean
  size?: 'sm' | 'md' | 'lg'
  onChange?: (rating: number) => void
}

export default function StarRating({ rating, interactive = false, size = 'md', onChange }: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState<number | null>(null)

  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }

  const sizeClass = sizes[size]
  const displayRating = hoverRating !== null ? hoverRating : rating

  const handleClick = (value: number) => {
    if (interactive && onChange) {
      onChange(value)
    }
  }

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(null)
    }
  }

  // Render a single star with partial fill support
  const renderStar = (position: number) => {
    // Calculate how much of this star should be filled (0 to 1)
    const fillPercentage = Math.max(0, Math.min(1, displayRating - position + 1))

    return (
      <div
        key={position}
        className={`relative ${sizeClass}`}
      >
        {/* Empty star (background) */}
        <svg
          className="absolute inset-0 text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>

        {/* Filled star (foreground) - clipped to show partial fill */}
        {fillPercentage > 0 && (
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${fillPercentage * 100}%` }}
          >
            <svg
              className="text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              style={{ width: `${(1 / fillPercentage) * 100}%` }}
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        )}
      </div>
    )
  }

  // Render interactive star with clickable left and right halves
  const renderInteractiveStar = (position: number) => {
    // Calculate how much of this star should be filled (0 to 1)
    const fillPercentage = Math.max(0, Math.min(1, displayRating - position + 1))

    return (
      <div
        key={position}
        className={`relative ${sizeClass} cursor-pointer`}
      >
        {/* Empty star (background) */}
        <svg
          className="absolute inset-0 text-gray-300 pointer-events-none"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>

        {/* Filled star (foreground) - clipped to show partial fill */}
        {fillPercentage > 0 && (
          <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{ width: `${fillPercentage * 100}%` }}
          >
            <svg
              className="text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              style={{ width: `${(1 / fillPercentage) * 100}%` }}
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        )}

        {/* Invisible clickable areas for left (half star) and right (full star) */}
        <div className="absolute inset-0 flex">
          {/* Left half - click for X.5 rating */}
          <div
            className="w-1/2 h-full"
            onMouseEnter={() => setHoverRating(position - 0.5)}
            onClick={() => handleClick(position - 0.5)}
          />
          {/* Right half - click for X.0 rating */}
          <div
            className="w-1/2 h-full"
            onMouseEnter={() => setHoverRating(position)}
            onClick={() => handleClick(position)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1" onMouseLeave={handleMouseLeave}>
      {interactive
        ? [1, 2, 3, 4, 5].map(pos => renderInteractiveStar(pos))
        : [1, 2, 3, 4, 5].map(pos => renderStar(pos))
      }
    </div>
  )
}
